import { notifyApiError, type Text } from '@/shared/data/feedback';
import { useNotifier } from '@/shared/data/notifier';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, type QueryKey, type UseQueryOptions } from '@tanstack/react-query';
import { useBlocker } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import { useForm, type DefaultValues, type FieldValues, type UseFormReturn } from 'react-hook-form';
import type { z } from 'zod';

import { useApiMutation, type FeedbackMessage } from '@/shared/data';
import { ApiError, type FieldError } from '@/shared/http';
import { usePermissions, type PermissionResource } from '@/shared/permissions';
import { can } from '@/shared/screen';

import { applyApiFieldErrors } from './api-errors';

/** Id de rota do modo criar, como no legado (97 comparações com `'novo'`, 06 P07). */
export const NEW_RECORD_ID = 'novo';

export type FormMode = 'create' | 'edit';

export interface FormContext {
  readonly mode: FormMode;
  readonly id: string | undefined;
}

/** Local de navegação que o guarda compara (o mesmo formato do `useBlocker`). */
export interface GuardLocation {
  readonly pathname: string;
  readonly search: Readonly<Record<string, unknown>>;
}

export interface UseFormScreenOptions<
  V extends FieldValues,
  R,
  B,
  Res,
  K extends QueryKey = QueryKey,
> {
  /** `'novo'` (ou ausente) cria; qualquer outro valor edita esse registro. */
  readonly id: string | undefined;
  readonly schema: z.ZodType<V, V>;
  /** Valores do modo criar. */
  readonly empty: V;
  /** `queryOptions` do detalhe (a mesma chave da tela de leitura). */
  readonly detail?: (id: string) => UseQueryOptions<R, ApiError, R, K>;
  /** Registro do fio → valores do formulário (o `*.mappers.ts` do módulo). */
  readonly toForm?: (record: R) => V;
  /** Valores → corpo do fio, no formato do legado (U02, U21). */
  readonly toWire: (values: V, context: FormContext) => B;
  readonly save: (body: B, context: FormContext) => Promise<Res>;
  /** Recurso da permissão: `Create` no criar, `Update` no editar; sem ela, somente leitura. */
  readonly permission?: { readonly resource: string };
  readonly readOnly?: boolean;
  readonly success?: { readonly create?: FeedbackMessage; readonly edit?: FeedbackMessage };
  readonly invalidate?: readonly QueryKey[];
  /** Depois de salvar (e de o formulário voltar a limpo): navegar para o registro, fechar… */
  readonly onSaved?: (result: Res, context: FormContext) => unknown;
  /** Quais navegações saem do formulário (o guarda só pergunta nelas). Padrão: mudar caminho ou busca. */
  readonly leaves?: (current: GuardLocation, next: GuardLocation) => boolean;
  /**
   * Toast do erro da API no salvar. Padrão (X11): só sem erro de campo, com o título padrão. `always` avisa
   * também com erro de campo, e `title` troca o título, para a tela cujo legado sempre avisa (ex.: "Atenção!").
   */
  readonly errorFeedback?: { readonly title?: Text; readonly always?: boolean };
}

export interface FormGuard {
  readonly blocked: boolean;
  /** Sai mesmo assim, descartando o que mudou. */
  readonly proceed: () => void;
  /** Fica no formulário. */
  readonly stay: () => void;
}

export interface FormScreen<V extends FieldValues, R> {
  readonly form: UseFormReturn<V>;
  readonly mode: FormMode;
  readonly id: string | undefined;
  readonly readOnly: boolean;
  /** Carregando o registro ou as permissões. */
  readonly isLoading: boolean;
  readonly loadError: ApiError | undefined;
  readonly reload: () => void;
  readonly record: R | undefined;
  readonly submit: (event?: { preventDefault?: () => void }) => Promise<void>;
  readonly isSubmitting: boolean;
  /** Erro da última tentativa de salvar (o toast já saiu pela regra do legado). */
  readonly apiError: ApiError | undefined;
  /** Erros de campo da API que não casaram com nenhum campo da tela. */
  readonly unmatchedErrors: readonly FieldError[];
  readonly isDirty: boolean;
  readonly guard: FormGuard;
  /** Muda a cada tentativa com erro (validação ou API): o `FormScreen` foca o primeiro campo inválido. */
  readonly errorEpoch: number;
}

/** Chave do overlay na URL (`useOverlayParam`): abrir ou fechar um sheet não sai do formulário. */
const OVERLAY_KEY = 'sheet';

function sameSearch(a: Readonly<Record<string, unknown>>, b: Readonly<Record<string, unknown>>) {
  const { [OVERLAY_KEY]: _a, ...restA } = a;
  const { [OVERLAY_KEY]: _b, ...restB } = b;
  return JSON.stringify(restA) === JSON.stringify(restB);
}

const defaultLeaves = (current: GuardLocation, next: GuardLocation) =>
  current.pathname !== next.pathname || !sameSearch(current.search, next.search);

/**
 * Criar e editar com react-hook-form + zod (P07, U09): carrega o registro, decide somente leitura
 * pela permissão, salva com o feedback padrão (`useApiMutation`), põe o erro de API no campo
 * (`INVALID_FORM`) e guarda a saída com alterações não salvas.
 */
export function useFormScreen<
  V extends FieldValues,
  R = unknown,
  B = unknown,
  Res = unknown,
  K extends QueryKey = QueryKey,
>(options: UseFormScreenOptions<V, R, B, Res, K>): FormScreen<V, R> {
  const id = options.id === NEW_RECORD_ID || options.id === '' ? undefined : options.id;
  const mode: FormMode = id === undefined ? 'create' : 'edit';
  const context: FormContext = { mode, id };
  const permissions = usePermissions();

  const detailOptions = id !== undefined && options.detail ? options.detail(id) : undefined;
  const detail = useQuery<R, ApiError, R>({
    ...((detailOptions as UseQueryOptions<R, ApiError, R> | undefined) ?? {
      queryKey: ['form-screen', 'none'],
      queryFn: () => Promise.reject(new Error('sem detalhe')),
    }),
    enabled: detailOptions !== undefined,
  });
  const record = detailOptions ? detail.data : undefined;
  const loaded = record !== undefined && options.toForm ? options.toForm(record) : undefined;

  const form = useForm<V, unknown, V>({
    resolver: zodResolver(options.schema),
    defaultValues: options.empty as DefaultValues<V>,
    ...(loaded ? { values: loaded, resetOptions: { keepDirtyValues: true } } : {}),
    mode: 'onTouched',
    shouldFocusError: false,
  });
  const { isDirty } = form.formState;

  const resource = options.permission?.resource;
  const required =
    resource === undefined
      ? undefined
      : mode === 'create'
        ? can(resource, 'Create')
        : can(resource, 'Update');
  const allowed =
    required === undefined ||
    permissions.can(required.resource as PermissionResource, required.action);
  const readOnly =
    options.readOnly === true || (required !== undefined && permissions.isSuccess && !allowed);

  const notifier = useNotifier();
  const [unmatched, setUnmatched] = useState<readonly FieldError[]>([]);
  const [errorEpoch, setErrorEpoch] = useState(0);
  const leaving = useRef(false);
  const successMessage = mode === 'create' ? options.success?.create : options.success?.edit;
  const mutation = useApiMutation<Res, B>({
    mutationFn: (body) => options.save(body, context),
    error: false,
    ...(successMessage ? { success: successMessage } : {}),
    ...(options.invalidate ? { invalidate: options.invalidate } : {}),
  });

  const leaves = options.leaves ?? defaultLeaves;
  const blocker = useBlocker({
    shouldBlockFn: ({ current, next }) =>
      isDirty && !readOnly && !leaving.current && leaves(current, next),
    enableBeforeUnload: () => isDirty && !readOnly && !leaving.current,
    withResolver: true,
  });

  const submit = async (event?: { preventDefault?: () => void }) => {
    event?.preventDefault?.();
    if (readOnly) return;
    setUnmatched([]);
    await form.handleSubmit(
      async (values) => {
        try {
          const result = await mutation.mutateAsync(options.toWire(values, context));
          form.reset(values);
          leaving.current = true;
          try {
            await options.onSaved?.(result, context);
          } finally {
            leaving.current = false;
          }
        } catch (error) {
          const apiError = ApiError.from(error);
          if (!apiError.isCanceled) {
            setUnmatched(applyApiFieldErrors(form, apiError.fieldErrors));
            if (options.errorFeedback?.always === true || apiError.fieldErrors.length === 0) {
              notifyApiError(notifier, apiError, options.errorFeedback?.title);
            }
            setErrorEpoch((epoch) => epoch + 1);
          }
        }
      },
      () => setErrorEpoch((epoch) => epoch + 1),
    )();
  };

  return {
    form,
    mode,
    id,
    readOnly,
    isLoading:
      permissions.isPending || (detailOptions !== undefined && detail.isPending && !detail.isError),
    loadError: detailOptions && detail.isError ? detail.error : undefined,
    reload: () => void detail.refetch(),
    record,
    submit,
    isSubmitting: mutation.isPending,
    apiError: mutation.error ?? undefined,
    unmatchedErrors: unmatched,
    isDirty,
    errorEpoch,
    guard: {
      blocked: blocker.status === 'blocked',
      proceed: () => blocker.proceed?.(),
      stay: () => blocker.reset?.(),
    },
  };
}
